import type { ReactNode } from 'react'
import logo from '../assets/defenssive-logo.png'
import { FlowStepper } from './FlowStepper'
import './flowStyles'

/**
 * What the product reads, one line each. Taken from the published product brief (docs/legal/product-brief.md,
 * "What it checks") and kept to its words: this panel sits on the sign-in page, where a visitor decides
 * whether to trust the product, so it makes no claim the brief does not make. In particular it carries no
 * score, no count of services and no sample numbers -- the page it replaced the mock of showed a "74" and
 * "Services 06", and an invented figure here would contradict the brief, which lists what is NOT assessed.
 */
const AREAS: readonly { name: string; reads: string }[] = [
  { name: 'Identity', reads: 'Conditional Access, multi-factor authentication, privileged roles' },
  { name: 'Email', reads: 'SPF, DKIM, DMARC, automatic forwarding, mail flow rules' },
  { name: 'Endpoint', reads: 'Defender health, known exploitable vulnerabilities, Intune compliance' },
  { name: 'Data', reads: 'SharePoint and OneDrive sharing settings, not your files' },
  { name: 'Cloud', reads: 'Azure subscription configuration, on the Monitor plan' },
]

/**
 * The brand frame around the sign-in screens: a dark panel saying what the product is, beside the form.
 *
 * Presentation only. It wraps the existing screens and changes none of their behaviour, copy or checks,
 * and it adds no role=status or role=alert, which the sign-in notices rely on being unique on the page.
 * Under 860px the panel collapses to a brand bar so the form is the first thing on a phone.
 *
 * No external font or image: the server's policy allows only its own origin (style-src, font-src and
 * img-src 'self'), and loading a third-party font would tell that provider about every visitor.
 */
export function SignInShell({ children }: { children: ReactNode }) {
  return (
    <div className="signin-shell">
      <aside className="signin-brand" aria-label="About Defenssive">
        <div className="signin-brand-head">
          <img className="signin-logo" src={logo} alt="" width={36} height={36} />
          <span className="signin-wordmark">Defenssive</span>
        </div>

        <div className="signin-brand-body">
          <p className="signin-kicker">Security assessment for Microsoft 365</p>
          <p className="signin-headline">
            Know what to fix <span>first</span> in your Microsoft 365.
          </p>
          <p className="signin-lede">
            Defenssive reads your Microsoft 365 and Entra ID configuration, compares it with the CISA SCuBA
            baselines and with additional checks of our own, and tells you in plain language what to fix first.
          </p>

          <ul className="signin-areas">
            {AREAS.map((area) => (
              <li key={area.name}>
                <b>{area.name}</b>
                <span>{area.reads}</span>
              </li>
            ))}
          </ul>

          <ul className="signin-points">
            <li>
              <b>Reads, never writes.</b> It makes no write calls to your tenant.
            </li>
            <li>
              <b>Honest about gaps.</b> What it could not read is shown as that, never as a pass.
            </li>
            <li>
              <b>Your administrator stays in control.</b> Access is approved on Microsoft&apos;s own consent
              screen.
            </li>
          </ul>

          {/* Plain <a>: Caddy serves /product as a static page outside the application. */}
          <a className="signin-more" href="/product" target="_blank" rel="noopener">
            Read exactly which permissions it asks for
          </a>
        </div>

        <p className="signin-brand-foot">Defenssive Cybersecurity LLC · pilot phase</p>
      </aside>

      <main className="signin-main">
        <div className="signin-column flow-scope">
          {/* A fact, not an adjective: there is no password anywhere in the product (magic link or Entra). */}
          <p className="signin-secure">
            <svg viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.8" aria-hidden="true">
              <path d="M10 2 17 5v4c0 4.3-3 7.1-7 8.7C6 16.1 3 13.3 3 9V5l7-3Z" />
              <path d="m7 9.5 2 2 4-4" />
            </svg>
            Passwordless sign-in
          </p>
          <FlowStepper at={0} />
          {children}
        </div>
      </main>
    </div>
  )
}
